import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    LinearProgress,
    Chip,
    IconButton,
    Tooltip,
    Collapse,
} from '@material-ui/core';
import PublishIcon from '@material-ui/icons/Publish';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import SpeedIcon from '@material-ui/icons/Speed';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { baseUrl } from '../../util/host';
import axios from 'axios';
import moment from 'moment-timezone';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        backgroundColor: '#f5f5f5',
        minHeight: 'calc(100vh - 64px)',
    },
    header: {
        marginBottom: theme.spacing(3),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    titleIcon: {
        color: '#3f51b5',
        fontSize: 32,
        marginRight: theme.spacing(1),
    },
    title: {
        fontSize: '28px',
        fontWeight: 400,
        color: '#333',
    },
    refreshButton: {
        backgroundColor: '#e0e0e0',
        color: '#666',
        '&:hover': {
            backgroundColor: '#d5d5d5',
        },
    },
    tableContainer: {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
        borderRadius: theme.spacing(1),
    },
    tableHead: {
        backgroundColor: '#fafafa',
    },
    tableHeadCell: {
        fontWeight: 600,
        color: '#333',
        fontSize: '14px',
        borderBottom: '2px solid #e0e0e0',
    },
    tableRow: {
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
    tableCell: {
        padding: theme.spacing(2),
        fontSize: '14px',
        color: '#333',
    },
    jobId: {
        fontFamily: 'monospace',
        fontSize: '13px',
        color: '#666',
        backgroundColor: '#f0f0f0',
        padding: theme.spacing(0.5, 1),
        borderRadius: 4,
    },
    progressContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 200,
    },
    progressBar: {
        flex: 1,
        height: 8,
        borderRadius: 4,
    },
    progressBarCompleted: {
        backgroundColor: '#e8f5e9',
        '& .MuiLinearProgress-bar': {
            backgroundColor: '#4caf50',
        },
    },
    progressBarInProgress: {
        backgroundColor: '#e3f2fd',
        '& .MuiLinearProgress-bar': {
            backgroundColor: '#2196f3',
        },
    },
    progressBarFailed: {
        backgroundColor: '#ffebee',
        '& .MuiLinearProgress-bar': {
            backgroundColor: '#f44336',
        },
    },
    progressText: {
        fontSize: '13px',
        fontWeight: 500,
        minWidth: 45,
        textAlign: 'right',
    },
    statusChip: {
        fontSize: '12px',
        height: 28,
    },
    statusCompleted: {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
        '& .MuiChip-icon': {
            color: '#2e7d32',
        },
    },
    statusInProgress: {
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
        '& .MuiChip-icon': {
            color: '#1565c0',
        },
    },
    statusFailed: {
        backgroundColor: '#ffebee',
        color: '#c62828',
        '& .MuiChip-icon': {
            color: '#c62828',
        },
    },
    statusPending: {
        backgroundColor: '#fff3e0',
        color: '#e65100',
        '& .MuiChip-icon': {
            color: '#e65100',
        },
    },
    dateTime: {
        fontSize: '13px',
        color: '#666',
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(8),
        color: '#999',
        backgroundColor: 'white',
        borderRadius: theme.spacing(1),
    },
    emptyIcon: {
        fontSize: 64,
        color: '#ddd',
        marginBottom: 16,
    },
    userEmail: {
        fontSize: '13px',
        color: '#666',
    },
    sectionContainer: {
        marginBottom: theme.spacing(4),
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
        paddingBottom: theme.spacing(1),
        borderBottom: '2px solid #e0e0e0',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
            backgroundColor: '#fafafa',
        },
    },
    sectionHeaderLeft: {
        display: 'flex',
        alignItems: 'center',
    },
    sectionIcon: {
        fontSize: 24,
        marginRight: theme.spacing(1),
        color: '#3f51b5',
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: 500,
        color: '#333',
    },
    toggleIcon: {
        color: '#666',
        transition: 'transform 0.3s ease',
    },
    jobCount: {
        fontSize: '14px',
        color: '#999',
        marginLeft: theme.spacing(1),
        fontWeight: 400,
    },
    sectionEmptyState: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: '#999',
        backgroundColor: 'white',
        borderRadius: theme.spacing(1),
    },
    jobType: {
        fontSize: '13px',
        color: '#666',
    },
}));

const JobsActivity = () => {
    const classes = useStyles();
    const [bulkSlaJobs, setBulkSlaJobs] = useState([]);
    const [publishJobs, setPublishJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [publishJobsExpanded, setPublishJobsExpanded] = useState(true);
    const [bulkSlaJobsExpanded, setBulkSlaJobsExpanded] = useState(true);

    const fetchJobsList = useCallback(async () => {
        try {
            const res = await axios.get(`${baseUrl("REACT_APP_API_URL")}/api/address/jobs`);
            console.log('res', res);
            setPublishJobs(res.data.publishJobs || [])
            setBulkSlaJobs(res.data.bulkSlaJobs || [])
            setLoading(false);
        } catch (err) {
            console.error('Error fetching published jobs:', err);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchJobsList();
    }, [fetchJobsList]);

    const handleRefresh = () => {
        setLoading(true);
        fetchJobsList();
    };

    const isJobRecent = (createdAt) => {
        const thirtyMinutesAgo = new Date(Date.now() - 1000 * 60 * 30);
        return new Date(createdAt) > thirtyMinutesAgo;
    };

    const getStatusIcon = (percentageCompleted = 0, createdAt = new Date()) => {
        if(percentageCompleted === 100) {
            return <CheckCircleIcon />;
        } else if(percentageCompleted >= 0 && isJobRecent(createdAt)) {
            return <HourglassEmptyIcon />;
        } else {
            return <ErrorIcon />;
        }
    }

    const getStatusChipClass = (percentageCompleted = 0, createdAt = new Date()) => {
        if (percentageCompleted === 100) {
            return classes.statusCompleted;
        } else if (percentageCompleted >= 0 && isJobRecent(createdAt)) {
            return classes.statusInProgress;
        } else {
            return classes.statusFailed;
        }
    };

    const getProgressBarClass = (percentageCompleted = 0, createdAt = new Date()) => {
        if (percentageCompleted === 100) {
            return classes.progressBarCompleted;
        } else if (percentageCompleted >= 0 && isJobRecent(createdAt)) {
            return classes.progressBarInProgress;
        } else {
            return classes.progressBarFailed;
        }
    };

    const formatStatus = (percentageCompleted = 0, createdAt = new Date()) => {
        if(percentageCompleted === 100) {
            return 'Completed';
        } else if(percentageCompleted >= 0 && isJobRecent(createdAt)) {
            return 'In Progress';
        } else {
            return 'Failed';
        }
    }

    const renderJobsTable = (jobs) => (
        <TableContainer component={Paper} className={classes.tableContainer}>
            <Table>
                <TableHead className={classes.tableHead}>
                    <TableRow>
                        <TableCell className={classes.tableHeadCell}>#</TableCell>
                        <TableCell className={classes.tableHeadCell}>Job ID</TableCell>
                        <TableCell className={classes.tableHeadCell}>Status</TableCell>
                        <TableCell className={classes.tableHeadCell}>Progress</TableCell>
                        <TableCell className={classes.tableHeadCell}>Published By</TableCell>
                        <TableCell className={classes.tableHeadCell}>Created At</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {jobs.map((job, index) => (
                        <TableRow key={job.jobId || job._id || index} className={classes.tableRow}>
                            <TableCell className={classes.tableCell}>
                                {index + 1}
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                                <span className={classes.jobId}>
                                    {job.jobId || job._id || 'N/A'}
                                </span>
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                                <Chip
                                    icon={getStatusIcon(+job.percentageCompleted || 0, job.createdAt || job.created_at)}
                                    label={formatStatus(+job.percentageCompleted || 0, job.createdAt || job.created_at)}
                                    className={`${classes.statusChip} ${getStatusChipClass(+job.percentageCompleted || 0, job.createdAt || job.created_at)}`}
                                    size="small"
                                />
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                                <Box className={classes.progressContainer}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={+job.percentageCompleted || 0}
                                        className={`${classes.progressBar} ${getProgressBarClass(+job.percentageCompleted || 0)}`}
                                    />
                                    <span className={classes.progressText}>
                                        {+job.percentageCompleted || 0}%
                                    </span>
                                </Box>
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                                <span className={classes.userEmail}>
                                    {job.publishedBy || 'N/A'}
                                </span>
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                                <span className={classes.dateTime}>
                                    {moment(job.createdAt || job.created_at || job.publishedOn).format('DD/MM/YYYY HH:mm:ss')}
                                </span>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderEmptyState = (icon, message) => (
        <Paper className={classes.sectionEmptyState}>
            {icon}
            <Typography variant="body1" color="textSecondary">
                {message}
            </Typography>
        </Paper>
    );

    return (
        <div className={classes.root}>
            {/* Page Header */}
            <Box className={classes.header}>
                <Box className={classes.titleContainer}>
                    <PublishIcon className={classes.titleIcon} />
                    <Typography className={classes.title}>
                        Jobs Activity
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton 
                        className={classes.refreshButton}
                        onClick={handleRefresh}
                        disabled={loading}
                    >
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {loading ? (
                <Paper className={classes.tableContainer}>
                    <LinearProgress />
                </Paper>
            ) : (
                <>
                    {/* Published Jobs Section */}
                    <Box className={classes.sectionContainer}>
                        <Box 
                            className={classes.sectionHeader}
                            onClick={() => setPublishJobsExpanded(!publishJobsExpanded)}
                        >
                            <Box className={classes.sectionHeaderLeft}>
                                <PublishIcon className={classes.sectionIcon} />
                                <Typography className={classes.sectionTitle}>
                                    Published Jobs
                                </Typography>
                                <Typography className={classes.jobCount}>
                                    ({publishJobs.length})
                                </Typography>
                            </Box>
                            {publishJobsExpanded ? (
                                <ExpandLessIcon className={classes.toggleIcon} />
                            ) : (
                                <ExpandMoreIcon className={classes.toggleIcon} />
                            )}
                        </Box>
                        <Collapse in={publishJobsExpanded}>
                            {publishJobs.length > 0 ? (
                                renderJobsTable(publishJobs)
                            ) : (
                                renderEmptyState(
                                    <PublishIcon style={{ fontSize: 48, color: '#ddd', marginBottom: 8 }} />,
                                    'No published jobs found'
                                )
                            )}
                        </Collapse>
                    </Box>

                    {/* Bulk SLA Jobs Section */}
                    <Box className={classes.sectionContainer}>
                        <Box 
                            className={classes.sectionHeader}
                            onClick={() => setBulkSlaJobsExpanded(!bulkSlaJobsExpanded)}
                        >
                            <Box className={classes.sectionHeaderLeft}>
                                <SpeedIcon className={classes.sectionIcon} />
                                <Typography className={classes.sectionTitle}>
                                    Bulk SLA Jobs
                                </Typography>
                                <Typography className={classes.jobCount}>
                                    ({bulkSlaJobs.length})
                                </Typography>
                            </Box>
                            {bulkSlaJobsExpanded ? (
                                <ExpandLessIcon className={classes.toggleIcon} />
                            ) : (
                                <ExpandMoreIcon className={classes.toggleIcon} />
                            )}
                        </Box>
                        <Collapse in={bulkSlaJobsExpanded}>
                            {bulkSlaJobs.length > 0 ? (
                                renderJobsTable(bulkSlaJobs)
                            ) : (
                                renderEmptyState(
                                    <SpeedIcon style={{ fontSize: 48, color: '#ddd', marginBottom: 8 }} />,
                                    'No bulk SLA jobs found'
                                )
                            )}
                        </Collapse>
                    </Box>
                </>
            )}
        </div>
    );
};

export default JobsActivity;

